import { ReactElement } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getPostBySlug } from './posts';
import SEO from '../../components/SEO';

function renderContent(content: string, theme: any) {
  const lines = content.trim().split('\n');
  const elements: ReactElement[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={key++} style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.textPrimary, margin: '40px 0 16px' }}>
          {line.replace('## ', '')}
        </h2>
      );
    } else if (line.startsWith('**') && line.endsWith('**') && !line.slice(2, -2).includes('**')) {
      const text = line.slice(2, -2);
      elements.push(
        <p key={key++} style={{ color: theme.colors.textPrimary, fontSize: '17px', lineHeight: 1.8, marginBottom: '16px', fontWeight: 600 }}>
          {text}
        </p>
      );
    } else if (line.startsWith('- ')) {
      elements.push(
        <li key={key++} style={{ color: theme.colors.textSecondary, fontSize: '17px', lineHeight: 1.8, marginBottom: '8px', marginLeft: '24px' }}>
          {line.replace('- ', '')}
        </li>
      );
    } else if (line.startsWith('✅ ')) {
      const parts = line.replace('✅ ', '').split(' — ');
      elements.push(
        <div key={key++} style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-start' }}>
          <span style={{ color: '#10B981', fontSize: '18px', flexShrink: 0 }}>✅</span>
          <p style={{ color: theme.colors.textSecondary, fontSize: '17px', lineHeight: 1.7, margin: 0 }}>
            {parts[0] && <strong style={{ color: theme.colors.textPrimary }}>{parts[0]}</strong>}
            {parts[1] && ` — ${parts[1]}`}
          </p>
        </div>
      );
    } else if (line.startsWith('*') && line.endsWith('*') && !line.slice(1, -1).includes('*')) {
      elements.push(
        <p key={key++} style={{ color: theme.colors.textSecondary, fontSize: '16px', lineHeight: 1.8, marginBottom: '16px', fontStyle: 'italic' }}>
          {line.slice(1, -1)}
        </p>
      );
    } else {
      // Handle inline bold **text**
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const rendered = parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx} style={{ color: theme.colors.textPrimary }}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      elements.push(
        <p key={key++} style={{ color: theme.colors.textSecondary, fontSize: '17px', lineHeight: 1.8, marginBottom: '20px' }}>
          {rendered}
        </p>
      );
    }
  }

  return elements;
}

export default function BlogPost() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug || '');

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgPrimary, color: theme.colors.textPrimary }}>
      <SEO
        title={`${post.title} | Trubonn Blog`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
      />

      {/* Nav */}
      <nav style={{
        padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`, position: 'sticky', top: 0,
        background: theme.colors.bgPrimary, zIndex: 1000,
      }}>
        <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src="/logo.svg" alt="Trubonn Logo" style={{ height: '70px', width: '180px' }} />
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button onClick={() => navigate('/blog')} style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '15px', padding: '8px 16px' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}>
            ← Blog
          </button>
          <button onClick={() => navigate('/pricing')} style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '15px', padding: '8px 16px' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}>
            Pricing
          </button>
        </div>
      </nav>

      {/* Article */}
      <article style={{ maxWidth: '740px', margin: '0 auto', padding: '80px 24px' }}>

        {/* Tags */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
          {post.tags.slice(0, 3).map((tag) => (
            <span key={tag} style={{
              background: `${theme.colors.brand}20`, color: theme.colors.brand,
              fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '8px',
            }}>
              {tag}
            </span>
          ))}
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '42px', fontWeight: 800, marginBottom: '24px', lineHeight: 1.2,
          color: theme.colors.textPrimary,
        }}>
          {post.title}
        </h1>

        {/* Meta */}
        <div style={{ display: 'flex', gap: '24px', color: theme.colors.textTertiary, fontSize: '14px', marginBottom: '48px', flexWrap: 'wrap' }}>
          <span>📅 {post.date}</span>
          <span>⏱️ {post.readTime}</span>
          <span>✍️ {post.author}</span>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: theme.colors.border, marginBottom: '48px' }} />

        {/* Content */}
        <div>
          {renderContent(post.content, theme)}
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: theme.colors.border, margin: '48px 0' }} />

        {/* CTA */}
        <div style={{
          background: `${theme.colors.brand}10`,
          border: `1px solid ${theme.colors.brand}30`,
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center',
        }}>
          <h3 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.textPrimary, marginBottom: '12px' }}>
            Ready to keep your files in Australia?
          </h3>
          <p style={{ color: theme.colors.textSecondary, fontSize: '16px', marginBottom: '28px', lineHeight: 1.6 }}>
            Try Trubonn free for 7 days. Australian hosted, no data mining, Privacy Act compliant.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            style={{
              background: theme.gradients.brand, border: 'none', color: '#fff',
              padding: '14px 36px', borderRadius: '10px', fontSize: '16px',
              fontWeight: 600, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(157, 78, 221, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Start Free Trial →
          </button>
        </div>

        {/* Back link */}
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <button
            onClick={() => navigate('/blog')}
            style={{ background: 'transparent', border: 'none', color: theme.colors.brand, cursor: 'pointer', fontSize: '15px' }}
          >
            ← Back to Blog
          </button>
        </div>
      </article>

      {/* Footer */}
      <footer style={{ padding: '40px', textAlign: 'center', borderTop: `1px solid ${theme.colors.border}`, background: theme.colors.bgPrimary }}>
        <p style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>© 2026 Trubonn Systems. All rights reserved.</p>
        <div style={{ marginTop: '16px', display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="/privacy" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</a>
          <a href="/terms" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>Terms of Service</a>
          <a href="/contact" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>Contact</a>
        </div>
      </footer>
    </div>
  );
}
